import type { Bucket } from './frames';
import { DEFAULT_FRAME } from './frames';

/**
 * What this reader, in this browser, has set the dashboard to.
 *
 * All of it is a PREFERENCE and none of it is shared. The card ORDER is the
 * opposite — it lives on dispatch's `priority` column and moves for everybody,
 * including the Flutter page — and that line is what decides where a setting
 * belongs: a colleague opening the same board should see the same cards in the
 * same order, and has no business inheriting how wide somebody else likes them.
 *
 * Every read and write is wrapped, because `localStorage` throws rather than
 * returning null in a private window or with site data blocked, and a
 * preference is never worth a blank screen.
 */

export type Density = 'wide' | 'medium' | 'narrow';

export interface DashboardPrefs {
  frame: string;
  /** The grouping override, remembered WITH the frame it was chosen for. */
  bucket: Bucket | null;
  density: Density;
  /** Whether the page re-reads on a timer. */
  live: boolean;
}

const KEY = 'sphere.dashboard.prefs';

/** What pass one stored: the frame key, alone, as a bare string. */
const LEGACY_KEY = 'sphere.dashboard.frame';

const DENSITIES: Density[] = ['wide', 'medium', 'narrow'];
const BUCKETS: Bucket[] = ['day', 'week', 'month', 'quarter', 'year'];

export const DEFAULT_PREFS: DashboardPrefs = {
  frame: DEFAULT_FRAME,
  bucket: null,
  density: 'medium',
  // On, like the Flutter dashboard — an operations board that does not move is
  // read once and then believed for an hour. What bounds the cost is the pause
  // while the tab is hidden, not this being off.
  live: true,
};

/** The minimum a chart card may be, which is what decides how many fit a row. */
export const DENSITY_MIN_PX: Record<Density, number> = {
  wide: 560,
  medium: 400,
  narrow: 300,
};

export const DENSITY_LABELS: Record<Density, string> = {
  wide: 'Wide',
  medium: 'Medium',
  narrow: 'Narrow',
};

const asDensity = (value: unknown): Density =>
  DENSITIES.includes(value as Density) ? (value as Density) : DEFAULT_PREFS.density;

const asBucket = (value: unknown): Bucket | null =>
  BUCKETS.includes(value as Bucket) ? (value as Bucket) : null;

/**
 * Read them, tolerating everything: no storage, a throw, a half-written blob,
 * and the bare frame string pass one wrote before there was anything else to
 * remember.
 */
export const readPrefs = (): DashboardPrefs => {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<DashboardPrefs>;
      return {
        frame: typeof parsed.frame === 'string' && parsed.frame ? parsed.frame : DEFAULT_PREFS.frame,
        bucket: asBucket(parsed.bucket),
        density: asDensity(parsed.density),
        live: typeof parsed.live === 'boolean' ? parsed.live : DEFAULT_PREFS.live,
      };
    }
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) return { ...DEFAULT_PREFS, frame: legacy };
  } catch {
    // A private window, blocked site data, or something that is not JSON.
  }
  return { ...DEFAULT_PREFS };
};

export const writePrefs = (prefs: DashboardPrefs): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // The page works; it just forgets.
  }
};

/**
 * How often the page re-reads itself, in ms.
 *
 * 90 seconds, which is the dashboard's own `dashboardAutoRefreshPeriod`. It
 * refreshes each source round-robin within that period; here the whole page is
 * one batch of reads, so the period is the interval.
 */
export const LIVE_INTERVAL_MS = 90_000;

/**
 * The widest window that keeps itself up to date.
 *
 * Three days. Past that the refresh is spending somebody else's service on a
 * figure that cannot visibly move: one more job on a thirty-day count changes
 * the third digit, and the dispatch row's read is the whole job board. Live
 * numbers are what a dispatcher wants from today and this week, so that is
 * where the timer runs.
 *
 * The toggle is left ON and simply does not fire, rather than being switched
 * off behind the operator's back — they get their live page again the moment
 * they pick a short window, without having to remember what they had set.
 */
export const LIVE_MAX_DAYS = 3;
